import { prop, pick } from 'ramda';
import React, { Suspense, useEffect } from 'react';
import { Switch, useParams } from 'react-router-dom';

import { Page } from 'js/components/system/Page';
import { Route } from 'js/components/common/Route';
import { usePrevious } from 'js/hooks/usePrevious';
import { CaptchaPage } from 'js/components/main/CaptchaPage';
import { getCleanPath, getURLParam } from 'js/utils/url.util';
import { componentService } from 'js/services/component.service';
import { SEO_LINK_TYPE } from 'js/components/system/Page/constants';
import { GamesInfoPopupLauncher } from 'js/components/popups/GamesInfoPopup';
import { PageInPopup } from 'js/components/system/Page/components/PageInPopup';
import { useAppSelector, usePageSelector, useUserSelector } from 'js/store/hooks';
import { QuickDepositPage } from 'js/components/system/Page/components/QuickDepositPage';
import { useUpdateUserLocationChat } from 'js/components/system/CommunicationWidgets/hooks';

import { LaunchGame } from './components/LaunchGame';

export const PageRouter = () => {
    const { role } = useUserSelector(pick(['role']));
    const {
        rootPage,
        pageInPopupUrls,
        quickDepositPage,
        lottoRootURL,
        sportsbookRootURL,
        dynamicRoutingUrls,
        quickDepositUseCustom
    } = useAppSelector(
        pick([
            'rootPage',
            'pageInPopupUrls',
            'quickDepositPage',
            'lottoRootURL',
            'sportsbookRootURL',
            'dynamicRoutingUrls',
            'quickDepositUseCustom'
        ])
    );

    const { path: pageRouterPath, pathLang } = useParams();
    const updateUserLocationChat = useUpdateUserLocationChat();
    const popupBackgroundPagePath = usePageSelector(prop('popupBackgroundPagePath'));

    const cleanPath = getCleanPath(pageRouterPath || '/');
    const pagePath = cleanPath === '/' ? rootPage : cleanPath;

    if (!pageInPopupUrls.includes('/') && pageInPopupUrls?.includes(rootPage)) {
        pageInPopupUrls.push('/');
    }

    const parentPagePath = getCleanPath(popupBackgroundPagePath || rootPage);

    const prevPath = usePrevious(pagePath);

    useEffect(() => {
        if (pagePath !== prevPath) {
            updateUserLocationChat();
        }
    }, [pagePath, prevPath, updateUserLocationChat]);

    const isPageAsPopUpDisabled = getURLParam(location.search, 'noPageAsPopup') === '1' || role === 'admin';

    // can't use map inside switch since "key" for iterable elements breaks the cache
    const { dynamicPageURLs, dynamicPageInPopupURLs } = dynamicRoutingUrls.reduce((acc, dynamicPath) => {
        if (dynamicPath === sportsbookRootURL || dynamicPath === lottoRootURL) {
            return acc;
        }

        const routePageURL = `/:pathLang(${pathLang})?${dynamicPath}/:param1?/:param2?/:param3?/:param4?/:param5?/:other(.*)?`;
        const isShownAsPopup = !isPageAsPopUpDisabled && pageInPopupUrls && pageInPopupUrls.includes(dynamicPath);

        isShownAsPopup ? acc.dynamicPageInPopupURLs.push(routePageURL) : acc.dynamicPageURLs.push(routePageURL);

        return acc;
    }, { dynamicPageURLs: [], dynamicPageInPopupURLs: [] });

    const pagesInPopup = pageInPopupUrls?.reduce((acc, pageInPopupUrl) => {
        if (isPageAsPopUpDisabled || dynamicRoutingUrls.includes(pageInPopupUrl)) {
            return acc;
        }

        acc.push(`/:pathLang(${pathLang})?${pageInPopupUrl}`);

        return acc;
    }, []);

    return (
        <Switch>
            <Route path={`/:pathLang(${pathLang})?/igaming/:gameCode`}>
                <Page path={parentPagePath} isPopupBackgroundPage={true} seoLinkType={SEO_LINK_TYPE.CURRENT_PATH} />
                <LaunchGame />
            </Route>

            <Route path={`/:pathLang(${pathLang})?/online-game/:gameCode`}>
                <Page path={parentPagePath} isPopupBackgroundPage={true} seoLinkType={SEO_LINK_TYPE.CURRENT_PATH} />
                <GamesInfoPopupLauncher />
            </Route>

            {quickDepositPage && !quickDepositUseCustom && (
                <Route path={`/:pathLang(${pathLang})?${quickDepositPage}`}>
                    <Page path={parentPagePath} isPopupBackgroundPage={true} seoLinkType={SEO_LINK_TYPE.DISABLED} />
                    <QuickDepositPage path={pagePath} />
                </Route>
            )}

            {sportsbookRootURL && (
                <Route path={`/:pathLang(${pathLang})?${sportsbookRootURL}/*`}>
                    <Page path={sportsbookRootURL} seoLinkType={SEO_LINK_TYPE.WITH_DYNAMIC_SEGMENT} />
                </Route>
            )}

            {lottoRootURL && (
                <Route path={`/:pathLang(${pathLang})?${lottoRootURL}/*`}>
                    <Page path={lottoRootURL} seoLinkType={SEO_LINK_TYPE.WITH_DYNAMIC_SEGMENT} />
                </Route>
            )}

            {dynamicPageInPopupURLs.length && (
                <Route path={dynamicPageInPopupURLs}>
                    <Page path={parentPagePath} isPopupBackgroundPage={true} seoLinkType={SEO_LINK_TYPE.DISABLED} />
                    <PageInPopup path={pagePath} seoLinkType={SEO_LINK_TYPE.WITH_DYNAMIC_SEGMENT} />
                </Route>
            )}

            {dynamicPageURLs.length && (
                <Route path={dynamicPageURLs}>
                    <Page path={pagePath} seoLinkType={SEO_LINK_TYPE.WITH_DYNAMIC_SEGMENT} />
                </Route>
            )}

            {pagesInPopup.length && (
                <Route exact path={pagesInPopup}>
                    <Page path={parentPagePath} isPopupBackgroundPage={true} seoLinkType={SEO_LINK_TYPE.DISABLED} />
                    <PageInPopup path={pagePath} />
                </Route>
            )}

            <Route exact path={`/:pathLang(${pathLang})?/native/captcha`}>
                <Suspense fallback="">
                    <CaptchaPage />
                </Suspense>
            </Route>

            <Route>
                <Page path={pagePath} />
            </Route>
        </Switch>
    );
};

componentService.registerWebContentComponents({ PageRouter });
